// ==================================================================================
// Digital Approval System (DAS) - SearchableSelect
// ==================================================================================
// Lightweight searchable dropdown (combobox) styled to match the app's
// .form-select. Controlled by value/onChange over {value,label} options, with a
// type-to-filter search box. No external dependency.
// ==================================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Label for the "no selection" ('') entry, e.g. "All Types". Omit to hide it. */
  allOptionLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
}

const optionBase: React.CSSProperties = {
  padding: '0.4rem 0.75rem',
  cursor: 'pointer',
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  allOptionLabel,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const selectedLabel =
    value === ''
      ? allOptionLabel || placeholder
      : options.find((o) => o.value === value)?.label || value;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="form-select"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span
          style={{
            color: value === '' ? '#9ca3af' : 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedLabel}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 60,
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 260,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            style={{
              margin: 6,
              padding: '0.35rem 0.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '0.8rem',
            }}
          />
          <div style={{ overflowY: 'auto' }}>
            {allOptionLabel !== undefined && (
              <div
                onClick={() => select('')}
                onMouseEnter={(e) => {
                  if (value !== '') e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  if (value !== '') e.currentTarget.style.background = 'transparent';
                }}
                style={{
                  ...optionBase,
                  background: value === '' ? '#eff6ff' : 'transparent',
                  color: value === '' ? '#1d4ed8' : '#1f2937',
                }}
              >
                {allOptionLabel}
              </div>
            )}

            {filtered.length === 0 && (
              <div style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', fontSize: '0.8rem' }}>No matches</div>
            )}

            {filtered.map((o) => {
              const active = o.value === value;
              return (
                <div
                  key={o.value}
                  onClick={() => select(o.value)}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}
                  style={{
                    ...optionBase,
                    background: active ? '#eff6ff' : 'transparent',
                    color: active ? '#1d4ed8' : '#1f2937',
                  }}
                >
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
